import React from 'react';
import PropTypes from 'prop-types';

// dangerouslySetInnerHTML is used many times in this file, but is
// all done safely as it is not user input. It is needed for translation
// to function correctly, because of the behaviour of the scraper.
/* eslint-disable react/no-danger */


const LockoutMessage = () => {
	const resetURL = '/acc/p/';
	const faqURL = 'https://help.smapply.io/hc/en-us/articles/360032514674-General-Account-FAQ#h_f994ac93-1f6a-4ade-80f5-bb56644a3a20';
	return (
		<div>
			<p dangerouslySetInnerHTML={
				{
					__html: interpolate(
						gettext(
							'If you are having trouble signing into your account, '
							+ 'first try resetting your password <a href="%(url)s">here</a>.',
						),
						{ url: resetURL },
						true,
					),
				}
			}
			/>
			<p dangerouslySetInnerHTML={
				{
					__html: interpolate(
						gettext(
							'If you have tried this and haven\'t received an email, please see our help document on '
							+ '<a target="_blank" rel="noopener" href="%(url)s">'
							+ 'Why am I not receiving any emails from the site?'
							+ '<i class="nc-icon-mini arrows-e_share-26 ml-2" aria-hidden="true"></i></a>.',
						),
						{ url: faqURL },
						true,
					),
				}
			}
			/>
			<p>
				{gettext('If your account is locked, the support team cannot manually unlock it but it will be '
				+ 'automatically unlocked at a later time. Please try again later.')}
			</p>
		</div>
	);
};

const RegOtherMessage = () => {
	const url = 'https://help.smapply.io/hc/en-us/articles/360000921094-How-to-Request-Login-Assistance';
	return (
		<div dangerouslySetInnerHTML={
			{
				__html: interpolate(
					gettext(
						'In order for the support team to assist with any login or registration issues, we '
						+ 'need to discuss the issue with the affected user. Please ask the affected user '
						+ 'to contact us and we\'ll be happy to assist them. For more information, please see '
						+ '<a target="_blank" rel="noopener" href="%(url)s">How to request login '
						+ 'assistance<i class="nc-icon-mini arrows-e_share-26 ml-2" aria-hidden="true"></i></a>.',
					),
					{ url },
					true,
				),
			}
		}
		/>
	);
};

const FeatureSuggestionsMessage = () => {
	const clickIcon = 'help icon';
	return (
		<div>
			{gettext('Thank you for your interest in submitting a feature request! To do so, please follow '
			+ 'these steps:')}
			<ol>
				<li dangerouslySetInnerHTML={
					{
						__html: interpolate(
							gettext('From your <a href="/admin/dashboard/">site\'s dashboard</a>, in the top right '
							+ 'corner click on the %(clickIcon)s'),
							{ clickIcon },
							true,
						),
					}
				}
				/>
				<li>{gettext('Choose Request a feature')}</li>
			</ol>
		</div>
	);
};

const CSSMessage = () => {
	const url = 'https://help.smapply.io/hc/en-us/articles/115001446073-How-to-Access-the-CSS-Editor';
	return (
		<div>
			<p>
				{gettext('CSS can be used to customize the presentation of pages within your site, but this is the '
				+ 'responsibility of site administrators. The support team is not available to troubleshoot '
				+ 'customized CSS formatting and presentation.')}
			</p>
			<p dangerouslySetInnerHTML={
				{
					__html: interpolate(
						gettext('For more information on using the CSS editor, please see our on <a target="_blank" '
						+ 'rel="noopener" href="%(url)s">How to access the CSS Editor<i class="nc-icon-mini '
						+ 'arrows-e_share-26 ml-2" aria-hidden="true"></i></a>.'),
						{ url },
						true,
					),
				}
			}
			/>
		</div>
	);
};

const HowToMessage = () => {
	const url = 'https://help.smapply.io/hc';
	return (
		<div>
			<p dangerouslySetInnerHTML={
				{
					__html: interpolate(
						gettext('If you need help with building something new, updating site settings or are looking '
						+ 'to better understand how to manage your site, we recommend starting with our searchable '
						+ '<a target="_blank" rel="noopener" href="%(url)s">Help Center<i '
						+ 'class="nc-icon-mini arrows-e_share-26 ml-2" aria-hidden="true"></i></a>.'),
						{ url },
						true,
					),
				}
			}
			/>
			<p>
				{gettext('If you don\'t find what you are looking for or are encountering technical issues, please '
				+ 'submit a request to the support team for guidance.')}
			</p>
			<p>
				{gettext('If you are looking for one-on-one assistance or training, let us know and we will put you in '
				+ 'contact with our Customer Success team to discuss these paid options.')}
			</p>
		</div>
	);
};

const SomeoneElseMessage = () => {
	const url = 'https://help.smapply.io/hc/en-us/articles/115001660353';

	return (
		<div>
			<p>
				{gettext('To help another user that is having issues, please have them contact the support team '
				+ 'directly so that we can work with them.')}
			</p>
			<p dangerouslySetInnerHTML={
				{
					__html: interpolate(
						gettext('Please see <a target="_blank" rel="noopener" href="%(url)s">How to '
						+ 'Contact Support<i class="nc-icon-mini arrows-e_share-26 ml-2" aria-hidden="true"></i></a> '
						+ 'for more information on how the user can contact us.'),
						{ url },
						true,
					),
				}
			}
			/>
		</div>
	);
};

const ContactAdmin = () => {
	const url = 'https://help.smapply.io/hc/en-us/articles/360032514674-General-Account-FAQ#h_0768db99-27e7-4f45-ba4f-6650e4886cfa';

	return (
		<div>
			<p>
				{gettext('For help with this type of request you will need to contact the site\'s administrator.')}
			</p>
			<p dangerouslySetInnerHTML={
				{
					__html: interpolate(
						gettext('For instructions on how to contact the administrator, see <a target="_blank"'
						+ 'href="%(url)s">How to Contact the Site\'s Administrator.'
						+ '<i class="nc-icon-mini arrows-e_share-26 ml-2" aria-hidden="true"></i></a>'),
						{ url },
						true,
					),
				}
			}
			/>
		</div>
	);
};

const messagingEnum = Object.freeze({
	lockoutMessage: 1,
	regOtherMessage: 2,
	featureSuggestionsMessage: 3,
	cssMessage: 4,
	howToMessage: 5,
	someoneElseMessage: 6,
	ContactAdmin: 7,
});

export default messagingEnum;

export {
	LockoutMessage,
	RegOtherMessage,
	FeatureSuggestionsMessage,
	CSSMessage,
	HowToMessage,
	SomeoneElseMessage,
	ContactAdmin,
};
